import type { Chat, Message, MessageHistoryPage, Uuid } from '@liniya/api-client';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../api';
import { queryKeys } from '../query-keys';
import { notify } from '../store/ui';

/**
 * Chat history, newest page first, older pages loaded on demand.
 *
 * The server pages backwards from a sequence number rather than by offset, so
 * messages arriving while the user scrolls cannot shift the window and cause a
 * page to repeat or skip.
 */
export function useMessageHistory(chatId: Uuid | null) {
  return useInfiniteQuery<MessageHistoryPage>({
    queryKey: chatId ? queryKeys.messages(chatId) : ['messages', 'none'],
    enabled: Boolean(chatId),
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam, signal }) =>
      api.messages.history(chatId as Uuid, {
        ...(typeof pageParam === 'number' ? { beforeSequence: pageParam } : {}),
        signal,
      }),
    getNextPageParam: (lastPage) => lastPage.pagination.next_before_sequence ?? undefined,
    // Pages come back newest-last, and each fetched page is older than the one
    // before it, so the array is reversed to read top-to-bottom in time order.
    select: (data) => ({
      pages: [...data.pages].reverse(),
      pageParams: [...data.pageParams].reverse(),
    }),
    staleTime: 15_000,
  });
}

/** Flatten the paged history into the list the conversation renders. */
export function flattenHistory(data: { pages: MessageHistoryPage[] } | undefined): Message[] {
  if (!data) return [];
  const seen = new Set<string>();
  const messages: Message[] = [];
  for (const page of data.pages) {
    for (const message of page.messages) {
      if (seen.has(message.message_id)) continue;
      seen.add(message.message_id);
      messages.push(message);
    }
  }
  return messages.sort((left, right) => left.server_sequence - right.server_sequence);
}

export function useEditMessage(chatId: Uuid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, content }: { messageId: Uuid; content: string }) =>
      api.messages.edit(messageId, content),
    onSuccess: async () => {
      // The durable update event will arrive over the socket and patch the
      // cache; refreshing the chat list keeps the preview honest if the socket
      // happens to be down.
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
      void chatId;
    },
    onError: () => notify('Не удалось изменить сообщение.', 'error'),
  });
}

export function useDeleteMessage(chatId: Uuid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: Uuid) => api.messages.remove(messageId),
    onMutate: async (messageId) => {
      // Removing it straight away keeps the action feeling immediate; the
      // durable delete event confirms it for every other device.
      await queryClient.cancelQueries({ queryKey: queryKeys.messages(chatId) });
      const previous = queryClient.getQueryData(queryKeys.messages(chatId));
      queryClient.setQueryData<{ pages: MessageHistoryPage[]; pageParams: unknown[] }>(
        queryKeys.messages(chatId),
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  messages: page.messages.filter((item) => item.message_id !== messageId),
                })),
              }
            : current,
      );
      return { previous };
    },
    onError: (_error, _messageId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.messages(chatId), context.previous);
      }
      notify('Не удалось удалить сообщение.', 'error');
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
    },
  });
}


/**
 * Delete several chosen messages behind one confirmation.
 *
 * Not a loop over {@link useDeleteMessage}: that mutation snapshots the cache
 * in its own `onMutate`, so ten of them running together each capture a
 * different half-applied state, and one failure rolls back to a snapshot that
 * resurrects messages the others already removed. One snapshot, one rollback
 * and one message to the reader is both correct and quieter.
 *
 * The API deletes by id, so this is still N requests. They are allowed to
 * settle independently — a partial failure leaving the rest deleted is better
 * than an all-or-nothing rule the server cannot actually offer.
 */
export function useDeleteMessages(chatId: Uuid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageIds: readonly Uuid[]) => {
      const results = await Promise.allSettled(
        messageIds.map((messageId) => api.messages.remove(messageId)),
      );
      return { failed: results.filter((result) => result.status === 'rejected').length };
    },
    onMutate: async (messageIds) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.messages(chatId) });
      const previous = queryClient.getQueryData(queryKeys.messages(chatId));
      const removing = new Set(messageIds);
      queryClient.setQueryData<{ pages: MessageHistoryPage[]; pageParams: unknown[] }>(
        queryKeys.messages(chatId),
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  messages: page.messages.filter((item) => !removing.has(item.message_id)),
                })),
              }
            : current,
      );
      return { previous };
    },
    onSuccess: async ({ failed }, messageIds, context) => {
      if (failed > 0) {
        // Put everything back and let the server say which of them actually
        // went: guessing from the client would leave the list disagreeing
        // with the history the next reload fetches.
        if (context?.previous) {
          queryClient.setQueryData(queryKeys.messages(chatId), context.previous);
        }
        await queryClient.invalidateQueries({ queryKey: queryKeys.messages(chatId) });
        notify(
          messageIds.length === 1
            ? 'Не удалось удалить сообщение.'
            : failed === messageIds.length
              ? 'Не удалось удалить сообщения.'
              : `Не удалось удалить сообщений: ${failed}.`,
          'error',
        );
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.chats });
    },
    onError: (_error, _messageIds, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.messages(chatId), context.previous);
      }
      notify('Не удалось удалить сообщения.', 'error');
    },
  });
}

export function useToggleReaction(chatId: Uuid) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ messageId, emoji }: { messageId: Uuid; emoji: string }) =>
      api.messages.react(messageId, emoji),
    onSuccess: (reactions, variables) => {
      queryClient.setQueryData<{ pages: MessageHistoryPage[]; pageParams: unknown[] }>(
        queryKeys.messages(chatId),
        (current) =>
          current
            ? {
                ...current,
                pages: current.pages.map((page) => ({
                  ...page,
                  messages: page.messages.map((item) =>
                    item.message_id === variables.messageId ? { ...item, reactions } : item,
                  ),
                })),
              }
            : current,
      );
    },
    onError: () => notify('Не удалось поставить реакцию.', 'error'),
  });
}

export function useReportMessage() {
  return useMutation({
    mutationFn: (messageId: Uuid) => api.messages.report(messageId),
    onSuccess: () => notify('Жалоба отправлена на проверку'),
    onError: () => notify('Не удалось отправить жалобу.', 'error'),
  });
}

/** Clear the unread badge for a chat the user is now reading. */
export function markChatRead(
  queryClient: ReturnType<typeof useQueryClient>,
  chatId: Uuid,
  sequence: number,
): void {
  queryClient.setQueryData<Chat[]>(queryKeys.chats, (chats) =>
    chats?.map((chat) =>
      chat.id === chatId
        ? {
            ...chat,
            unread_count: 0,
            membership: {
              ...chat.membership,
              last_read_sequence: Math.max(chat.membership.last_read_sequence, sequence),
            },
          }
        : chat,
    ),
  );
}
